/**
 * Breadcrumbs — слот в Header для хлебных крошек.
 *
 * UX-J1 (docs/UX_PROCESS_SHELL_AND_RESULTS_V1.md § 4): формат
 *   App > Process > Search / Generation / …
 * с первой иконкой Home.
 *
 * Реактивно реагирует на текущий pathname; вложенность — только по
 * первому сегменту (/process/* → «Process», /files/* → «Files», и т.д.).
 */
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, ChevronRight } from 'lucide-react';

const SECTION_TITLES: Record<string, { titleKey: string; sub: Record<string, string> }> = {
  files: {
    titleKey: 'nav.files',
    sub: { upload: 'upload', transform: 'transform', results: 'results' },
  },
  process: {
    titleKey: 'nav.process',
    sub: {
      search: 'search',
      matching: 'matching',
      generation: 'generation',
      media: 'media',
      translations: 'translations',
      complex: 'complex',
    },
  },
  autoposting: {
    titleKey: 'nav.autoposting',
    sub: { social: 'social', articles: 'articles' },
  },
  logs: {
    titleKey: 'nav.logs',
    sub: {},
  },
  settings: {
    titleKey: 'nav.settings',
    sub: {
      profile: 'profile',
      projects: 'projects',
      billing: 'billing',
      system: 'system',
      tips: 'tips',
    },
  },
};

export function Breadcrumbs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  const [section, sub] = segments;
  const sectionDef = SECTION_TITLES[section];
  if (!sectionDef) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 text-sm text-surface-400"
    >
      <Link
        to="/"
        className="flex items-center gap-1 hover:text-surface-200 transition-colors"
        title={t('nav.home', { defaultValue: 'Home' })}
      >
        <Home className="w-4 h-4" />
      </Link>
      <ChevronRight className="w-3.5 h-3.5 text-surface-500" />
      <Link
        to={`/${section}`}
        className="hover:text-surface-200 transition-colors"
        title={t(sectionDef.titleKey, { defaultValue: section })}
      >
        {t(sectionDef.titleKey, { defaultValue: section })}
      </Link>
      {sub && sectionDef.sub[sub] && (
        <>
          <ChevronRight className="w-3.5 h-3.5 text-surface-500" />
          <span className="text-surface-200 font-medium">
            {t(`nav.${sectionDef.sub[sub]}`, { defaultValue: sub })}
          </span>
        </>
      )}
    </nav>
  );
}